import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Plus, 
  Bot, 
  Zap, 
  Globe, 
  FileText,
  Users as UsersIcon,
  LogOut,
  User,
  Trash2,
  Settings,
  Building,
  Crown,
  ShieldCheck,
  Shield,
  Layers,
  RefreshCw,
  Sparkles
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import Users from "@/pages/Users";
import Orgs from "@/pages/Orgs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

const iconMap = {
  zap: Zap,
  bot: Bot,
  globe: Globe,
  file: FileText,
};

// Login Component
function LoginPage({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [requires2FA, setRequires2FA] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [resending, setResending] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      
      // Check if 2FA is required
      if (response.data.requires_2fa) {
        setRequires2FA(true);
        toast.info("Verification code sent to your email");
      } else {
        localStorage.setItem('auth_token', response.data.access_token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        onLogin(response.data.user);
        toast.success(`Welcome back, ${response.data.user.name}!`);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    
    try {
      const response = await axios.post(`${API}/auth/verify-2fa`, { 
        email, 
        code: verificationCode 
      });
      
      localStorage.setItem('auth_token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      onLogin(response.data.user);
      toast.success(`Welcome back, ${response.data.user.name}!`);
    } catch (err) {
      setError(err.response?.data?.detail || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResending(true);
    setError("");
    
    try {
      await axios.post(`${API}/auth/resend-2fa?email=${encodeURIComponent(email)}`);
      toast.success("New verification code sent!");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to resend code");
    } finally {
      setResending(false);
    }
  };

  const handleBack = () => {
    setRequires2FA(false);
    setVerificationCode("");
    setError("");
  };

  // 2FA Verification Screen
  if (requires2FA) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950 p-4">
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-900 via-zinc-950 to-black"></div>
        
        <Card className="w-full max-w-md bg-zinc-900/90 border-zinc-800 backdrop-blur-xl relative z-10" data-testid="2fa-card">
          <CardHeader className="text-center pb-2">
            <div className="flex items-center justify-center gap-3 mb-4">
              <img src="/billportal-logo.png" alt="BillPortal" className="w-12 h-12" />
              <span className="text-2xl font-bold text-zinc-100">BillPortal</span>
            </div>
            <CardDescription className="text-zinc-400">
              Enter the 6-digit code sent to<br />
              <span className="text-zinc-200 font-medium">{email}</span>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleVerify2FA} className="space-y-4">
              <div>
                <Label className="text-xs text-zinc-500 uppercase">Verification Code</Label>
                <Input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  required
                  maxLength={6}
                  data-testid="2fa-code"
                  className="bg-zinc-800 border-zinc-700 text-zinc-100 mt-1 text-center text-2xl tracking-widest"
                />
              </div>
              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-md text-red-400 text-sm" data-testid="2fa-error">
                  {error}
                </div>
              )}
              <Button 
                type="submit" 
                className="w-full bg-white text-black hover:bg-zinc-200" 
                disabled={loading || verificationCode.length !== 6}
                data-testid="verify-btn"
              >
                {loading ? "Verifying..." : "Verify"}
              </Button>
              <div className="flex items-center justify-between text-sm">
                <button 
                  type="button" 
                  onClick={handleBack}
                  className="text-zinc-400 hover:text-zinc-200"
                >
                  ← Back to Login
                </button>
                <button 
                  type="button" 
                  onClick={handleResendCode}
                  disabled={resending}
                  className="text-purple-400 hover:text-purple-300"
                >
                  {resending ? "Sending..." : "Resend Code"}
                </button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Login Screen
  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-950 p-4">
      <div className="absolute inset-0 bg-gradient-to-br from-zinc-900 via-zinc-950 to-black"></div>
      
      <Card className="w-full max-w-md bg-zinc-900/90 border-zinc-800 backdrop-blur-xl relative z-10" data-testid="login-card">
        <CardHeader className="text-center pb-2">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img src="/billportal-logo.png" alt="BillPortal" className="w-12 h-12" />
            <span className="text-2xl font-bold text-zinc-100">BillPortal</span>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label className="text-xs text-zinc-500 uppercase">Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.io"
                required
                data-testid="login-email"
                className="bg-zinc-800 border-zinc-700 text-zinc-100 mt-1"
              />
            </div>
            <div>
              <Label className="text-xs text-zinc-500 uppercase">Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                data-testid="login-password"
                className="bg-zinc-800 border-zinc-700 text-zinc-100 mt-1"
              />
            </div>
            
            {error && (
              <div className="text-red-400 text-sm bg-red-950/30 border border-red-900/50 rounded-md px-3 py-2">
                {error}
              </div>
            )}
            
            <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full bg-white text-black hover:bg-zinc-200">
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

// Main Dashboard
function DashboardPage({ user, onLogout }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("bots");
  const [bots, setBots] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [userOrgs, setUserOrgs] = useState([]);
  const [showCreateBot, setShowCreateBot] = useState(false);
  const [newBot, setNewBot] = useState({ name: "", description: "", portal_url: "", icon: "bot", org_id: "" });
  const [loading, setLoading] = useState(false);

  // Check if user is restricted (org_viewer or viewer - should not see admin screens)
  const isRestrictedUser = user?.role === "viewer" || user?.role === "org_viewer";
  
  // Check if user can access admin features
  const canAccessAdmin = ["super_admin", "admin", "bot_creator"].includes(user?.role);

  const getRoleIcon = (role) => {
    if (role === "super_admin") return Crown;
    if (role === "admin") return ShieldCheck;
    return User;
  };

  const getRoleLabel = (role) => {
    const labels = {
      super_admin: "Super Admin",
      admin: "Admin",
      bot_creator: "Bot Creator",
      editor: "Editor",
      viewer: "Viewer"
    };
    return labels[role] || "User";
  };

  const fetchBots = async () => {
    try {
      const response = await api.get("/bots");
      setBots(response.data.bots);
    } catch (error) {
      console.error("Failed to fetch bots:", error);
    }
  };

  const fetchOrgs = async () => {
    try {
      const response = await api.get("/orgs");
      setOrgs(response.data.orgs);
    } catch (error) {
      console.error("Failed to fetch orgs:", error);
    }
  };

  const fetchUserOrgs = async () => {
    try {
      const response = await api.get("/my-orgs");
      setUserOrgs(response.data.orgs || []);
    } catch (error) {
      console.error("Failed to fetch user orgs:", error);
    }
  };

  const [twoFactorEnabled, setTwoFactorEnabled] = useState(user?.two_factor_enabled || false);

  const fetch2FAStatus = async () => {
    try {
      const response = await api.get("/users/me/2fa-status");
      setTwoFactorEnabled(response.data.two_factor_enabled);
    } catch (error) {
      console.error("Failed to fetch 2FA status:", error);
    }
  };

  const toggle2FA = async () => {
    try {
      const newStatus = !twoFactorEnabled;
      await api.put("/users/me/2fa", { enabled: newStatus });
      setTwoFactorEnabled(newStatus);
      toast.success(`Two-factor authentication ${newStatus ? "enabled" : "disabled"}`);
    } catch (error) {
      toast.error("Failed to update 2FA settings");
    }
  };

  useEffect(() => {
    // Redirect restricted users to their default org
    if (isRestrictedUser) {
      const redirectToDefaultOrg = async () => {
        try {
          const response = await api.get("/orgs/user/default");
          if (response.data.default_org) {
            navigate(`/org/${response.data.default_org.id}`);
          } else {
            // No default org assigned, fetch user's orgs
            const orgsResponse = await api.get("/my-orgs");
            const userOrgsList = orgsResponse.data.orgs || [];
            if (userOrgsList.length > 0) {
              navigate(`/org/${userOrgsList[0].org.id}`);
            } else {
              toast.error("No organization assigned. Contact your administrator.");
            }
          }
        } catch (error) {
          console.error("Failed to get default org:", error);
          // Fallback: try to get user's orgs
          try {
            const orgsResponse = await api.get("/my-orgs");
            const userOrgsList = orgsResponse.data.orgs || [];
            if (userOrgsList.length > 0) {
              navigate(`/org/${userOrgsList[0].org.id}`);
            }
          } catch (e) {
            console.error("Failed to fetch user orgs:", e);
          }
        }
      };
      redirectToDefaultOrg();
      return;
    }

    fetchBots();
    fetchOrgs();
    fetchUserOrgs();
    fetch2FAStatus();
    
    // Auto-refresh every 10 seconds when on Portals tab
    const interval = setInterval(() => {
      if (activeTab === "portals") {
        fetchBots();
      }
    }, 10000);
    
    return () => clearInterval(interval);
  }, [activeTab, isRestrictedUser, navigate]);

  const createBot = async () => {
    if (!newBot.name || !newBot.portal_url) {
      toast.error("Name and Portal URL are required");
      return;
    }
    
    setLoading(true);
    try {
      await api.post("/bots", newBot);
      toast.success("Portal created!");
      setShowCreateBot(false);
      setNewBot({ name: "", description: "", portal_url: "", icon: "bot", org_id: "" });
      fetchBots();
    } catch (error) {
      toast.error("Failed to create portal");
    } finally {
      setLoading(false);
    }
  };

  const deleteBot = async (botId, botName) => {
    if (!window.confirm(`Delete "${botName}" and all its data? This cannot be undone.`)) return;
    
    try {
      await api.delete(`/bots/${botId}`);
      toast.success("Bot deleted");
      fetchBots();
    } catch (error) {
      toast.error("Failed to delete bot");
    }
  };

  const RoleIcon = getRoleIcon(user?.role);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-zinc-900/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-3">
                <img src="/billportal-logo.png" alt="BillPortal" className="w-10 h-10" />
                <span className="text-xl font-bold text-white">BillPortal</span>
              </div>
              <div>
                <p className="text-xs text-zinc-500">Manage Portals and Collection Agents</p>
              </div>
            </div>
            
            {/* Navigation Tabs - Only for admin users */}
            {canAccessAdmin && (
            <div className="hidden md:flex items-center gap-1 bg-zinc-900 rounded-lg p-1 border border-zinc-800">
              <Button
                variant={activeTab === "bots" ? "default" : "ghost"}
                size="sm"
                onClick={() => setActiveTab("bots")}
                className={activeTab === "bots" ? "bg-zinc-800" : ""}
                data-testid="tab-portals"
              >
                <Globe className="w-4 h-4 mr-2" />
                Portals
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/studio")}
                className="text-purple-400 hover:text-purple-300"
                data-testid="tab-studio"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Agent Studio
              </Button>
              <Button
                variant={activeTab === "orgs" ? "default" : "ghost"}
                size="sm"
                onClick={() => setActiveTab("orgs")}
                className={activeTab === "orgs" ? "bg-zinc-800" : ""}
                data-testid="tab-orgs"
              >
                <Building className="w-4 h-4 mr-2" />
                Orgs
              </Button>
              <Button
                variant={activeTab === "users" ? "default" : "ghost"}
                size="sm"
                onClick={() => setActiveTab("users")}
                className={activeTab === "users" ? "bg-zinc-800" : ""}
                data-testid="tab-users"
              >
                <UsersIcon className="w-4 h-4 mr-2" />
                Users
              </Button>
            </div>
            )}
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" data-testid="user-menu-btn" className="bg-zinc-800 border-zinc-700">
                  <RoleIcon className="w-4 h-4 mr-2" />
                  <span className="hidden sm:inline">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800 w-56">
                <div className="px-2 py-1.5 border-b border-zinc-800">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-zinc-500">{user?.email}</p>
                  <Badge variant="outline" className="mt-1 text-xs">
                    {getRoleLabel(user?.role)}
                  </Badge>
                </div>
                
                {/* Org View Switch */}
                {userOrgs.length > 0 && (
                  <div className="border-b border-zinc-800 py-1">
                    <p className="px-2 py-1 text-xs text-zinc-500 uppercase">Switch to Org View</p>
                    {userOrgs.map((orgItem) => (
                      <DropdownMenuItem 
                        key={orgItem.org.id} 
                        onClick={() => navigate(`/org/${orgItem.org.id}`)}
                        className="cursor-pointer"
                        data-testid={`switch-org-${orgItem.org.id}`}
                      >
                        <Building className="w-4 h-4 mr-2 text-purple-400" />
                        {orgItem.org.name}
                        <span className="ml-auto text-xs text-zinc-500">{orgItem.role.replace('org_', '')}</span>
                      </DropdownMenuItem>
                    ))}
                  </div>
                )}
                
                {/* Security Settings */}
                <div className="border-b border-zinc-800 py-1">
                  <DropdownMenuItem 
                    onClick={toggle2FA}
                    className="cursor-pointer"
                    data-testid="toggle-2fa"
                  >
                    <Shield className="w-4 h-4 mr-2 text-emerald-400" />
                    Two-Factor Auth
                    <span className={`ml-auto text-xs px-2 py-0.5 rounded ${twoFactorEnabled ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-700 text-zinc-400'}`}>
                      {twoFactorEnabled ? "ON" : "OFF"}
                    </span>
                  </DropdownMenuItem>
                </div>
                
                <DropdownMenuItem onClick={onLogout} className="text-red-400 cursor-pointer mt-1" data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          
          {/* Mobile Navigation - Only for admin users */}
          {canAccessAdmin && (
          <div className="flex md:hidden items-center gap-1 mt-3 bg-zinc-900 rounded-lg p-1 border border-zinc-800 overflow-x-auto">
            <Button
              variant={activeTab === "bots" ? "default" : "ghost"}
              size="sm"
              onClick={() => setActiveTab("bots")}
              className={`flex-shrink-0 ${activeTab === "bots" ? "bg-zinc-800" : ""}`}
            >
              <Globe className="w-4 h-4 mr-1" />
              Portals
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/studio")}
              className="flex-shrink-0 text-purple-400"
            >
              <Sparkles className="w-4 h-4 mr-1" />
              Studio
            </Button>
            <Button
              variant={activeTab === "orgs" ? "default" : "ghost"}
              size="sm"
              onClick={() => setActiveTab("orgs")}
              className={`flex-shrink-0 ${activeTab === "orgs" ? "bg-zinc-800" : ""}`}
            >
              <Building className="w-4 h-4 mr-1" />
              Orgs
            </Button>
            <Button
              variant={activeTab === "users" ? "default" : "ghost"}
              size="sm"
              onClick={() => setActiveTab("users")}
              className={`flex-shrink-0 ${activeTab === "users" ? "bg-zinc-800" : ""}`}
            >
              <UsersIcon className="w-4 h-4 mr-1" />
              Users
            </Button>
          </div>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-6 py-8">
        {activeTab === "users" ? (
          <Users currentUser={user} />
        ) : activeTab === "orgs" ? (
          <Orgs currentUser={user} />
        ) : (
          <>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold">Portals</h2>
                <p className="text-zinc-500 text-sm">Manage your portal scrapers and collection agents</p>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={fetchBots} className="bg-zinc-800 border-zinc-700" data-testid="refresh-portals-btn">
                  <RefreshCw className="w-4 h-4" />
                </Button>
                <Button onClick={() => setShowCreateBot(true)} data-testid="create-portal-btn" className="bg-white text-black hover:bg-zinc-200">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Portal
                </Button>
              </div>
            </div>

            {/* Portals Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {bots.map((bot) => {
                const IconComponent = iconMap[bot.icon] || Globe;
                return (
                  <Card 
                    key={bot.id} 
                    className="bg-zinc-900 border-zinc-800 hover:border-zinc-700 transition-all cursor-pointer group"
                    data-testid={`portal-card-${bot.id}`}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div 
                          className="flex items-center gap-3 flex-1"
                          onClick={() => navigate(`/bot/${bot.id}`)}
                        >
                          <div className="w-12 h-12 rounded-lg bg-emerald-500/20 flex items-center justify-center group-hover:bg-emerald-500/30 transition-colors">
                            <IconComponent className="w-6 h-6 text-emerald-400" />
                          </div>
                          <div>
                            <CardTitle className="text-lg">{bot.name}</CardTitle>
                            <CardDescription className="text-xs text-zinc-500 truncate max-w-[180px]">
                              {bot.portal_url}
                            </CardDescription>
                          </div>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100 transition-opacity">
                              <Settings className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800">
                            <DropdownMenuItem 
                              onClick={() => deleteBot(bot.id, bot.name)} 
                              className="text-red-400 cursor-pointer"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete Portal
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </CardHeader>
                    <CardContent onClick={() => navigate(`/bot/${bot.id}`)}>
                      {bot.org_name && (
                        <Badge variant="outline" className="bg-purple-950/30 text-purple-400 border-purple-900/50 text-xs mb-3">
                          <Building className="w-3 h-3 mr-1" />
                          {bot.org_name}
                        </Badge>
                      )}
                      {bot.description && (
                        <p className="text-sm text-zinc-400 mb-4 line-clamp-2">{bot.description}</p>
                      )}
                      <div className="flex items-center gap-4 text-xs">
                        <div className="flex items-center gap-1 text-zinc-500">
                          <Bot className="w-3 h-3" />
                          <span>{bot.login_count} agents</span>
                        </div>
                        <div className="flex items-center gap-1 text-zinc-500">
                          <FileText className="w-3 h-3" />
                          <span>{bot.bill_count?.toLocaleString() || 0} bills</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
              
              {bots.length === 0 && (
                <Card className="bg-zinc-900/50 border-zinc-800 border-dashed col-span-full">
                  <CardContent className="py-12 text-center">
                    <Globe className="w-12 h-12 mx-auto mb-4 text-zinc-700" />
                    <h3 className="text-lg font-medium text-zinc-400 mb-2">No portals yet</h3>
                    <p className="text-sm text-zinc-600 mb-4">Add your first portal to start scraping</p>
                    <Button onClick={() => setShowCreateBot(true)} variant="outline" className="bg-zinc-800 border-zinc-700">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Portal
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          </>
        )}
      </main>

      {/* Create Portal Dialog */}
      <Dialog open={showCreateBot} onOpenChange={setShowCreateBot}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md" data-testid="create-portal-dialog">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">Add New Portal</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-zinc-400">Portal Name</Label>
              <Input
                value={newBot.name}
                onChange={(e) => setNewBot({ ...newBot, name: e.target.value })}
                placeholder="e.g., Constellation Portal"
                className="bg-zinc-800 border-zinc-700 mt-1"
                data-testid="portal-name-input"
              />
            </div>
            <div>
              <Label className="text-zinc-400">Portal URL</Label>
              <Input
                value={newBot.portal_url}
                onChange={(e) => setNewBot({ ...newBot, portal_url: e.target.value })}
                placeholder="https://portal.example.com"
                className="bg-zinc-800 border-zinc-700 mt-1"
                data-testid="portal-url-input"
              />
            </div>
            <div>
              <Label className="text-zinc-400">Organization</Label>
              <Select 
                value={newBot.org_id} 
                onValueChange={(value) => setNewBot({ ...newBot, org_id: value })}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700 mt-1" data-testid="portal-org-select">
                  <SelectValue placeholder="Select organization (optional)" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="" className="cursor-pointer">
                    <span className="text-zinc-500">No organization</span>
                  </SelectItem>
                  {orgs.map(org => (
                    <SelectItem key={org.id} value={org.id} className="cursor-pointer">
                      <div className="flex items-center gap-2">
                        <Building className="w-4 h-4 text-purple-400" />
                        {org.name}
                        <span className="text-xs text-zinc-500">({org.account_id})</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-zinc-600 mt-1">Bills scraped will be assigned to this org</p>
            </div>
            <div>
              <Label className="text-zinc-400">Description (optional)</Label>
              <Input
                value={newBot.description}
                onChange={(e) => setNewBot({ ...newBot, description: e.target.value })}
                placeholder="What does this portal provide?"
                className="bg-zinc-800 border-zinc-700 mt-1"
              />
            </div>
            <div>
              <Label className="text-zinc-400">Icon</Label>
              <div className="flex gap-2 mt-2">
                {Object.entries(iconMap).map(([key, Icon]) => (
                  <Button
                    key={key}
                    type="button"
                    variant={newBot.icon === key ? "default" : "outline"}
                    size="sm"
                    onClick={() => setNewBot({ ...newBot, icon: key })}
                    className={newBot.icon === key ? "bg-emerald-600" : "bg-zinc-800 border-zinc-700"}
                  >
                    <Icon className="w-4 h-4" />
                  </Button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateBot(false)} className="bg-zinc-800 border-zinc-700">
              Cancel
            </Button>
            <Button onClick={createBot} disabled={loading} className="bg-white text-black hover:bg-zinc-200" data-testid="create-portal-submit">
              {loading ? "Adding..." : "Add Portal"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Main Export
export default function Dashboard() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    const savedUser = localStorage.getItem('user');
    
    if (token && savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (e) {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    setUser(null);
    toast.info("Signed out successfully");
  };

  if (loading) {
    return <div className="min-h-screen bg-zinc-950 flex items-center justify-center"><div className="text-zinc-500">Loading...</div></div>;
  }

  if (!user) {
    return <LoginPage onLogin={setUser} />;
  }

  return <DashboardPage user={user} onLogout={handleLogout} />;
}
